<template>
    <div v-if="plan" class="userPlan" :class="`userPlan--${variant}`">
        <!-- Background Decorativo -->
        <div class="userPlan_background">
            <div class="userPlan_orb userPlan_orb--1"></div>
            <div class="userPlan_orb userPlan_orb--2"></div>
        </div>

        <div class="userPlan_current">
            <div v-if="showLabel" class="userPlan_label">
                Plano
            </div>
            <PlanBadge
                :plan="plan"
                size="medium"
                :showIcon="true"
            />
        </div>

        <!-- Upgrade Button Premium - Apenas no sidebar -->
        <div v-if="variant === 'default'" class="userPlan_upgrade" @click="handleUpgradeClick">
            <div class="userPlan_upgradeGlow"></div>
            <div class="userPlan_upgradeContent">
                <div class="userPlan_upgradeIcon">
                    <sw-icon name="rocket" />
                </div>
                <div class="userPlan_upgradeTexts">
                    <span class="userPlan_upgradeTitle">Evoluir Plano</span>
                    <span class="userPlan_upgradeSubtitle">Recursos Premium</span>
                </div>
                <div class="userPlan_upgradeArrow">
                    <sw-icon name="arrow-right" />
                </div>
            </div>
            <div class="userPlan_upgradeShine"></div>
        </div>

        <!-- Recursos Section - Apenas no default -->
        <div v-if="variant === 'default'" class="userPlan_resources">
            <div class="userPlan_resourcesHeader" @click="toggleResources">
                <div class="userPlan_resourcesLabel">
                    <sw-icon name="layers" />
                    <span>Recursos</span>
                </div>
                <div class="userPlan_resourcesToggle" :class="{ 'userPlan_resourcesToggle--open': showResources }">
                    <sw-icon name="arrow-down" />
                </div>
            </div>

            <div v-if="showResources" class="userPlan_resourcesList">
                <div class="userPlan_resourceItem">
                    <div class="userPlan_resourceInfo">
                        <span class="userPlan_resourceName">Armazenamento</span>
                        <div class="userPlan_resourceUsage">
                            <span class="userPlan_resourceCurrent">{{ resources.storage.used }}GB</span>
                            <span class="userPlan_resourceSeparator">/</span>
                            <span class="userPlan_resourceTotal">{{ resources.storage.total }}GB</span>
                        </div>
                        <div class="userPlan_resourceBar">
                            <div class="userPlan_resourceProgress" :style="{ width: getUsagePercentage('storage') + '%' }"></div>
                        </div>
                    </div>
                </div>

                <div class="userPlan_resourceItem">
                    <div class="userPlan_resourceInfo">
                        <span class="userPlan_resourceName">Envios</span>
                        <div class="userPlan_resourceUsage">
                            <span class="userPlan_resourceCurrent">{{ resources.uploads.used }}</span>
                            <span class="userPlan_resourceSeparator">/</span>
                            <span class="userPlan_resourceTotal">{{ resources.uploads.total }}</span>
                        </div>
                        <div class="userPlan_resourceBar">
                            <div class="userPlan_resourceProgress" :style="{ width: getUsagePercentage('uploads') + '%' }"></div>
                        </div>
                    </div>
                </div>

                <div class="userPlan_resourceItem">
                    <div class="userPlan_resourceInfo">
                        <span class="userPlan_resourceName">Alertas</span>
                        <div class="userPlan_resourceUsage">
                            <span class="userPlan_resourceCurrent">{{ resources.alerts.used }}</span>
                            <span class="userPlan_resourceSeparator">/</span>
                            <span class="userPlan_resourceTotal">{{ resources.alerts.total }}</span>
                        </div>
                        <div class="userPlan_resourceBar">
                            <div class="userPlan_resourceProgress" :style="{ width: getUsagePercentage('alerts') + '%' }"></div>
                        </div>
                    </div>
                </div>

                <div class="userPlan_resourceItem">
                    <div class="userPlan_resourceInfo">
                        <span class="userPlan_resourceName">Páginas de Compartilhamento</span>
                        <div class="userPlan_resourceUsage">
                            <span class="userPlan_resourceCurrent">{{ resources.sharePages.used }}</span>
                            <span class="userPlan_resourceSeparator">/</span>
                            <span class="userPlan_resourceTotal">{{ resources.sharePages.total }}</span>
                        </div>
                        <div class="userPlan_resourceBar">
                            <div class="userPlan_resourceProgress" :style="{ width: getUsagePercentage('sharePages') + '%' }"></div>
                        </div>
                    </div>
                </div>

                <div class="userPlan_resourceItem">
                    <div class="userPlan_resourceInfo">
                        <span class="userPlan_resourceName">Páginas de Upload</span>
                        <div class="userPlan_resourceUsage">
                            <span class="userPlan_resourceCurrent">{{ resources.uploadPages.used }}</span>
                            <span class="userPlan_resourceSeparator">/</span>
                            <span class="userPlan_resourceTotal">{{ resources.uploadPages.total }}</span>
                        </div>
                        <div class="userPlan_resourceBar">
                            <div class="userPlan_resourceProgress" :style="{ width: getUsagePercentage('uploadPages') + '%' }"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
	import { ref, computed } from 'vue';
	import PlanBadge from './PlanBadge.vue';
	import { mockPlanResources } from '@/data/mockData';

	const props = defineProps({
		plan: {
			type: String,
			required: true
		},
		variant: {
			type: String,
			default: 'default'
		},
		showLabel: {
			type: Boolean,
			default: true
		}
	});

	const emit = defineEmits(['upgrade-click']);

	const showResources = ref(false);

	// Recursos baseados no mock data centralizado
	const resources = computed(() => {
		return mockPlanResources[props.plan] || mockPlanResources.free;
	});

	const getUsagePercentage = (resource) => {
		const data = resources.value[resource];
		return Math.min((data.used / data.total) * 100, 100);
	};

	const toggleResources = () => {
		showResources.value = !showResources.value;
	};

	const handleUpgradeClick = () => {
		emit('upgrade-click');
	};
</script>

<style lang="scss" scoped>
/* ===========================
   CONTAINER PRINCIPAL
   =========================== */
.userPlan {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;
    padding: 1.5em;
    margin: 0 1.5em;
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9)),
        linear-gradient(135deg, #f1f5f9, #e2e8f0);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 1.25em;
    box-shadow:
        0 8px 32px rgba(148, 163, 184, 0.15),
        0 4px 16px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(16px);
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

}

/* ===========================
   BACKGROUND DECORATIVO
   =========================== */
.userPlan_background {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: 1.25em;
    z-index: 0;
}

.userPlan_orb {
    position: absolute;
    border-radius: 50%;
    opacity: 0.15;
    filter: blur(30px);
    animation: orbFloat 6s ease-in-out infinite alternate;

    &--1 {
        width: 80px;
        height: 80px;
        background: linear-gradient(45deg, #cbd5e1, #94a3b8);
        top: -40px;
        right: -40px;
        animation-delay: 0s;
    }

    &--2 {
        width: 60px;
        height: 60px;
        background: linear-gradient(45deg, #e2e8f0, #cbd5e1);
        bottom: -30px;
        left: -30px;
        animation-delay: -3s;
    }
}

/* ===========================
   SEÇÃO ATUAL DO PLANO
   =========================== */
.userPlan_current {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75em;
    z-index: 2;
}

.userPlan_label {
    font-size: 0.8125em;
    font-weight: 600;
    color: var(--textPrimary, #334155);
    letter-spacing: 0.025em;
    text-align: center;
}


/* ===========================
   BOTÃO DE UPGRADE PREMIUM
   =========================== */
.userPlan_upgrade {
    position: relative;
    width: 100%;
    border-radius: 1em;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 2;

    &:hover {
        transform: translateY(-3px) scale(1.02);

        .userPlan_upgradeGlow {
            opacity: 1;
        }

        .userPlan_upgradeIcon {
            transform: scale(1.15) rotate(10deg);
        }

        .userPlan_upgradeArrow {
            transform: translateX(4px);
        }

        .userPlan_upgradeShine {
            animation: shineWave 1s ease-out;
        }
    }

    &:active {
        transform: translateY(-1px) scale(1.01);
    }
}

.userPlan_upgradeGlow {
    position: absolute;
    inset: -2px;
    background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
    border-radius: 1em;
    opacity: 0.7;
    transition: opacity 0.3s ease;
    animation: borderGlow 3s ease-in-out infinite alternate;
}

.userPlan_upgradeContent {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1em 1.25em;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    border-radius: 0.875em;
    color: white;
    z-index: 1;
}

.userPlan_upgradeIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5em;
    height: 2.5em;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 0.75em;
    font-size: 1.125em;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.userPlan_upgradeTexts {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    margin-left: 1em;
}

.userPlan_upgradeTitle {
    font-size: 0.9375em;
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: 0.025em;
}

.userPlan_upgradeSubtitle {
    font-size: 0.75em;
    font-weight: 500;
    opacity: 0.9;
    line-height: 1.2;
}

.userPlan_upgradeArrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2em;
    height: 2em;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    font-size: 0.875em;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(10px);
}

.userPlan_upgradeShine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent);
    z-index: 2;
    pointer-events: none;
}

/* ===========================
   VARIANTE NAVBAR
   =========================== */
.userPlan--navbar {
    flex-direction: row;
    gap: 0;
    padding: 0;
    margin: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    backdrop-filter: none;

    .userPlan_background,
    .userPlan_upgrade {
        display: none;
    }

    .userPlan_badge {
        font-size: 0.6875em;
        padding: 0.25em 0.5em;
        border-radius: 0.75em;
        min-width: 60px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
        }
    }
}

/* ===========================
   ANIMAÇÕES
   =========================== */
@keyframes orbFloat {
    0% { transform: translate(0, 0) scale(1); }
    100% { transform: translate(-10px, 10px) scale(1.1); }
}

@keyframes borderGlow {
    0% { opacity: 0.7; }
    100% { opacity: 0.9; }
}

@keyframes shineWave {
    0% { left: -100%; }
    100% { left: 100%; }
}

/* ===========================
   SEÇÃO DE RECURSOS
   =========================== */
.userPlan_resources {
    position: relative;
    width: 100%;
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 1em;
    overflow: hidden;
    backdrop-filter: blur(8px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 2;
}

.userPlan_resourcesHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75em;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    border-radius: 1em;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: -1;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.9);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);

        &::before {
            opacity: 1;
        }

        .userPlan_resourcesLabel {
            .sw-icon {
                transform: scale(1.1);
                color: var(--primary, #667eea);
            }
        }

        .userPlan_resourcesToggle {
            color: var(--primary, #667eea);
            transform: scale(1.05);
        }
    }

    &:active {
        transform: translateY(0) scale(0.98);
        transition-duration: 0.1s;
    }
}

.userPlan_resourcesLabel {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-size: 0.8125em;
    font-weight: 700;
    color: var(--textPrimary, #334155);
    letter-spacing: 0.025em;

    .sw-icon {
        font-size: 1.125em;
        color: var(--primary, #667eea);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        filter: drop-shadow(0 1px 2px rgba(102, 126, 234, 0.2));
    }

    span {
        background: linear-gradient(135deg, var(--textPrimary, #334155), var(--textSecondary, #64748b));
        background-clip: text;
        -webkit-background-clip: text;
        transition: all 0.3s ease;
    }
}

.userPlan_resourcesToggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75em;
    height: 1.75em;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 0.5em;
    color: var(--textSecondary, #64748b);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .sw-icon {
        font-size: 0.625em;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &--open {
        background: linear-gradient(135deg, var(--primary, #667eea), var(--secondary, #764ba2));
        border-color: var(--primary, #667eea);
        color: white;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

        .sw-icon {
            transform: rotate(180deg);
        }
    }
}

.userPlan_resourcesList {
    padding: 0 0.75em 0.75em;
    display: flex;
    flex-direction: column;
    gap: 0.625em;
    animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
        max-height: 0;
    }
    to {
        opacity: 1;
        transform: translateY(0);
        max-height: 500px;
    }
}

.userPlan_resourceItem {
    display: flex;
    align-items: center;
    padding: 0.5em;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 0.5em;
    border: 1px solid rgba(255, 255, 255, 0.6);
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.95);
        transform: translateY(-1px);
    }
}

.userPlan_resourceInfo {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
}

.userPlan_resourceName {
    font-size: 0.75em;
    font-weight: 600;
    color: var(--textPrimary, #334155);
    line-height: 1.2;
}

.userPlan_resourceUsage {
    display: flex;
    align-items: center;
    gap: 0.25em;
    font-size: 0.6875em;
    font-weight: 500;
}

.userPlan_resourceCurrent {
    color: var(--primary, #667eea);
    font-weight: 700;
}

.userPlan_resourceSeparator {
    color: var(--textSecondary, #64748b);
}

.userPlan_resourceTotal {
    color: var(--textSecondary, #64748b);
}

.userPlan_resourceBar {
    width: 100%;
    height: 3px;
    background: rgba(100, 116, 139, 0.2);
    border-radius: 2px;
    overflow: hidden;
}

.userPlan_resourceProgress {
    height: 100%;
    background: linear-gradient(90deg, var(--primary, #667eea), var(--secondary, #764ba2));
    border-radius: 2px;
    transition: width 0.3s ease;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        animation: progressShine 2s infinite;
    }
}

@keyframes progressShine {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}
</style>